import { createContext, useContext, useEffect } from "react";

const STR = {
  en: {
    "nav.about": "About",
    "nav.services": "Services",
    "nav.record": "Field Record",
    "nav.credentials": "Credentials",
    "nav.contact": "Contact",
    "nav.writing": "Publications",
    "nav.book": "Book a Call",
    "hero.overline": "Hamdou Wane — Endpoint & Cloud Security Consultant",
    "hero.line1": "ZERO TRUST.",
    "hero.line2": "TOTAL CONTROL.",
    "hero.line3": "EVERY ENDPOINT.",
    "hero.sub": "Microsoft Intune, Defender for Endpoint, and Windows Autopilot — engineered for banks, aviation, and enterprises that cannot afford a breach. 7+ years. MD-102 certified, CompTIA Security+, InfraGard member.",
    "hero.cta1": "Book a Consultation",
    "hero.cta2": "Explore Services",
    "hero.stat1": "Years in the field",
    "hero.stat2": "Regulated industries",
    "hero.stat3": "Published technical deep dives",
    "marquee.items": ["MICROSOFT INTUNE", "DEFENDER FOR ENDPOINT", "WINDOWS AUTOPILOT", "ZERO TRUST ARCHITECTURE", "CLOUD SECURITY", "MDM / MAM", "COMPLIANCE & HARDENING", "ENTRA ID"],
    "nameplates.label": "Endpoint engineering experience across banking, aviation, insurance, manufacturing, and software.",
    "about.overline": "[ 02 — The Consultant ]",
    "about.h1a": "MEET HAMDOU",
    "about.h1b": "WANE",
    "about.bio1": "Seven years of endpoint engineering across regulated industries — banking, insurance, manufacturing, and enterprise software. Currently building endpoint and identity infrastructure at a major insurance carrier, with prior work spanning fleet management, zero-touch provisioning, and compliance-driven device policy.",
    "about.bio2": "Through ZTrust Africa, Hamdou brings enterprise-grade Microsoft Intune, Defender, and Autopilot expertise to organizations that need a fleet that deploys itself, defends itself, and passes every audit.",
    "about.available": "Available for consulting",
    "about.highlights": ["CompTIA Security+ certified", "MD-102 Endpoint Administrator", "InfraGard member — Cincinnati", "7+ years in the field"],
    "services.overline": "[ 03 — Capabilities ]",
    "services.h1": "THE ARSENAL",
    "services.desc": "Four disciplines. One objective: an endpoint estate that defends itself and deploys itself.",
    "services.items": [
      { title: "Microsoft Intune MDM", desc: "Full mobile device and application management. Enrollment strategy, compliance policies, configuration profiles, and Conditional Access policy design that survives audit.", tags: ["MDM / MAM", "Compliance", "Entra ID"] },
      { title: "Defender for Endpoint", desc: "Deployment, tuning, and response playbooks for Microsoft Defender. Attack surface reduction, EDR onboarding, and alert fatigue eliminated.", tags: ["EDR / XDR", "ASR Rules", "Threat Hunting"] },
      { title: "Windows Autopilot", desc: "Zero-touch provisioning at fleet scale. From hardware hash to productive device without a technician touching it. Ships direct to the user, no imaging infrastructure required.", tags: ["Zero-Touch", "ESP", "White Glove"] },
      { title: "Cloud Security", desc: "Zero-trust architecture across Microsoft 365 and Azure. Identity hardening, tenant baselines, and security posture reviews for regulated environments.", tags: ["Zero Trust", "Azure", "M365 Baseline"] },
    ],
    "chapters.overline": "[ 04 — Field Record ]",
    "chapters.h1a": "PROVEN WHERE",
    "chapters.h1b": "IT COUNTS",
    "chapters.items": [
      { org: "FIRST FINANCIAL BANK", role: "Endpoint Engineering — Financial Systems", body: "Hardened and managed endpoint fleets inside a regulated banking environment. Built compliance-driven Intune policies where an unmanaged device is not an inconvenience — it is an audit finding." },
      { org: "GE AEROSPACE", role: "Consultant — Shopfloor Migration", body: "Built Intune site profiles for the Shopfloor 2.0 migration across roughly 100 manufacturing sites and 10,000 devices — device configuration policies, dynamic groups, PowerShell scripting, and application packaging in a Government Cloud tenant." },
      { org: "CINCINNATI FINANCIAL CORPORATION", role: "Endpoint & Identity Engineering", body: "Intune, Configuration Manager, Autopilot, and Entra ID at enterprise scale. Windows 11 feature-update rollout, compliance policy design, and endpoint hardening in a regulated insurance environment." },
      { org: "HILLMAN GROUP", role: "Intune Engineer — Manufacturing", body: "Endpoint and identity engineering across a distributed manufacturing environment of 3,000+ devices. Configuration Manager, Intune, and Entra ID integration." },
      { org: "MRI SOFTWARE", role: "Identity & Access Administrator — Enterprise Software", body: "Identity and access administration for a global software organization — device management and endpoint operations." },
    ],
    "certs.overline": "[ 05 — Credentials ]",
    "certs.h1": "RECEIPTS",
    "certs.items": [
      { title: "MD-102", sub: "Microsoft 365 Certified: Endpoint Administrator Associate" },
      { title: "CompTIA Security+", sub: "Credly verified" },
      { title: "InfraGard", sub: "Member, Cincinnati chapter" },
      { title: "7+ Years", sub: "Regulated-industry field experience" },
    ],
    "certs.gallery": "[ Verified Credentials ]",
    "certs.g1title": "Microsoft 365 Certified: Endpoint Administrator Associate",
    "certs.g1meta": "Earned May 26, 2024 // Valid through May 26, 2027 // Online verifiable",
    "certs.g2title": "CompTIA Security+",
    "certs.g2meta": "Issued by CompTIA // Renewal in progress, Q4 2026",
    "certs.verify": "Verify →",
    "contact.overline": "[ 07 — Engage ]",
    "contact.h1a": "LET'S SECURE",
    "contact.h1b": "YOUR FLEET",
    "contact.intro": "Book a consultation slot or send an enquiry. Every message lands directly in Hamdou's command center — no gatekeepers, no ticket queues.",
    "contact.caption": "Principal Consultant — ZTrust Africa",
    "contact.location": "Cincinnati, OH — Remote Worldwide",
    "contact.tab1": "Book a Slot",
    "contact.tab2": "Enquiry",
    "contact.name": "Full name",
    "contact.email": "Work email",
    "contact.company": "Company (optional)",
    "contact.service": "Service",
    "contact.date": "Preferred date",
    "contact.time": "Time slot",
    "contact.notes": "Anything Hamdou should know? (optional)",
    "contact.message": "Tell me about your environment and what you need secured...",
    "contact.submitBooking": "Request Consultation Slot",
    "contact.submitEnquiry": "Send Enquiry",
    "contact.sending": "Sending...",
    "toast.booking": "Consultation requested. Hamdou will confirm your slot by email.",
    "toast.enquiry": "Enquiry sent. Expect a reply within one business day.",
    "toast.error": "Something went wrong. Please email contact@ztrustafrica.com directly.",
    "contact.pdf": "Download services sheet (PDF)",
    "writing.overline": "[ 01 — Publications ]",
    "writing.h1": "PUBLICATIONS",
    "writing.all": "All articles on Medium",
    "writing.items": [
      { title: "Deploying Windows Hello for Business with Cloud Kerberos Trust — A Technical Deep Dive", date: "May 3", url: "https://medium.com/@wanehamdou/deploying-windows-hello-for-business-with-cloud-kerberos-trust-a-technical-deep-dive-d7f99d1f0f28" },
      { title: "Why Your Hybrid Azure AD Devices Won't Enroll in Intune", date: "Jan 26", url: "https://medium.com/@wanehamdou/why-your-hybrid-azure-ad-devices-wont-enroll-in-intune-38adae8a7bc8" },
      { title: "Windows Autopilot Hybrid Azure AD Join: Troubleshooting Network Issues and Deployment Success 2/2", date: "Jul 24, 2025", url: "https://medium.com/@wanehamdou/windows-autopilot-hybrid-azure-ad-join-troubleshooting-network-issues-and-deployment-success-2-2-afa5bce4de42" },
      { title: "Detecting Reconnaissance with Honeytokens in Microsoft Defender for Identity", date: "Jun 29, 2025", url: "https://medium.com/@wanehamdou/detecting-reconnaissance-with-honeytokens-in-microsoft-defender-for-identity-10d3e67510cb" },
      { title: "Block User Sign-In from Non-Compliant Devices with a Conditional Access Policy", date: "Sep 7, 2024", url: "https://medium.com/@wanehamdou/block-user-sign-in-from-non-compliant-devices-with-a-conditional-access-policy-bee7a11493a9" },
    ],
    "engage.overline": "[ 06 — How We Work ]",
    "engage.h1": "HOW WE WORK",
    "engage.items": [
      { title: "Posture Review", meta: "1–2 weeks", desc: "Tenant assessment against a Zero Trust baseline, prioritized findings, and a remediation roadmap." },
      { title: "Deployment Project", meta: "4–12 weeks", desc: "Intune, Autopilot, or Conditional Access build-out — documented and handed over to your team." },
      { title: "Advisory Retainer", meta: "Monthly", desc: "Architecture review, escalation support, and policy design as you scale." },
    ],
    "engage.note": "Every engagement is scoped on a call — book yours below.",
    "footer.rights": "© 2026 ZTrust Africa — Hamdou Wane",
    "seo.title": "ZTrust Africa — Endpoint & Cloud Security Consulting",
    "seo.desc": "ZTrust Africa — Microsoft Intune, Defender, Autopilot and cloud security consulting by Hamdou Wane. 7+ years, Security+ and MD-102 certified. Cincinnati, OH — serving clients worldwide and across Africa.",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const lang = "en";
  const t = (key) => STR.en[key] ?? key;
  useEffect(() => {
    document.title = STR.en["seo.title"];
    document.documentElement.lang = "en";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", STR.en["seo.desc"]);
  }, []);
  return (
    <LanguageContext.Provider value={{ lang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
